/**
 * Assessment session helpers — single source of truth for the
 * KCIS™ Adaptive Ecosystem Conversion Engine client API.
 *
 * Design principles (Sprint B locked):
 *  - Frontend is a STATELESS RENDERER. No scoring, no branching, no routing here.
 *  - localStorage stores only an opaque session_id (UUID), never PII.
 *  - Session persistence survives refresh, accidental tab close, mobile suspension.
 *  - Designed for future i18n / voice / practitioner-handoff / AI reflection
 *    extensions without component rewrites.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "kcis_assessment_session_id";

const client = axios.create({
  baseURL: `${BACKEND_URL}/api/kcis/assessment`,
  timeout: 20000,
});

// ───────────────────── localStorage helpers ─────────────────────
export const sessionStore = {
  read() {
    try {
      return localStorage.getItem(STORAGE_KEY) || null;
    } catch {
      return null;
    }
  },
  write(id) {
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
    } catch {
      /* no-op (private mode, quota, etc.) */
    }
  },
  clear() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* no-op */
    }
  },
};

// ───────────────────── source detection (?source= fallback only) ─────────────────────
const KNOWN_SOURCES = new Set([
  "christyvonta.com",
  "kissedbycvon.com",
  "kisseduniversity.com",
]);

export function detectExplicitSource() {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = (params.get("source") || "").trim().toLowerCase();
    return KNOWN_SOURCES.has(raw) ? raw : null;
  } catch {
    return null;
  }
}

// ───────────────────── API ─────────────────────
export const assessmentApi = {
  async health() {
    const { data } = await client.get("/health");
    return data;
  },
  async start({ source } = {}) {
    const { data } = await client.post("/start", { source: source || null });
    sessionStore.write(data.session_id);
    return data;
  },
  async get(sessionId) {
    const { data } = await client.get(`/${sessionId}`);
    return data;
  },
  async submitStage(sessionId, stage, answers, extras = {}) {
    const stageKey = stage; // 'core' | 'tiebreaker' | 'pathway' | 'profile'
    const body = { answers, ...extras };
    const { data } = await client.post(`/${sessionId}/answer/${stageKey}`, body);
    return data;
  },
};

// ───────────────────── error helpers ─────────────────────
export function statusCodeOf(error) {
  return error?.response?.status ?? null;
}

export function isFeatureFlagOff(error) {
  return statusCodeOf(error) === 503;
}

export function isSessionGone(error) {
  const code = statusCodeOf(error);
  return code === 404 || code === 410;
}
